import type { ProjectGraphEdge, ProjectGraphReading } from '@avisoma/shared';

/** What the relationship's role says, in one line. A role added after this release reads as a plain link. */
export function roleExplanation(role: ProjectGraphEdge['role']): string {
  switch (role) {
    case 'subject':
      return 'The document is about this entity.';
    case 'mention':
      return 'The document names this entity.';
    default:
      return 'The document is linked to this entity.';
  }
}

/**
 * The source pages linked to the relationship, from every source, in order and deduped. The server folds a
 * pair's mention sources into its subject edge, so a page here need not show the role by itself.
 */
export function linkedPageIds(edge: ProjectGraphEdge): string[] {
  return [...new Set(edge.sources.flatMap((s) => s.pageIds))];
}

/** The readings the summary spells out; the rest count as "+N more". */
const SHOWN_READINGS = 3;

/** One reading in one line: P/N, S/N, TSN, CSN, ESN, each only when the row states it. */
export function readingText(reading: ProjectGraphReading): string {
  return [
    reading.partNumber === undefined ? undefined : `P/N ${reading.partNumber}`,
    reading.serialNumber === undefined ? undefined : `S/N ${reading.serialNumber}`,
    reading.tsn === undefined ? undefined : `TSN ${reading.tsn}`,
    reading.csn === undefined ? undefined : `CSN ${reading.csn}`,
    reading.esn === undefined ? undefined : `ESN ${reading.esn}`,
  ]
    .filter((piece): piece is string => piece !== undefined)
    .join(' · ');
}

/**
 * What the document's rows state about the entity, in one line: the first three distinct readings of every source,
 * then "+N more". Undefined when no source carries a reading. The values are as printed, evidence only.
 */
export function readingsSummary(edge: ProjectGraphEdge): string | undefined {
  const texts = [...new Set(edge.sources.flatMap((s) => (s.readings ?? []).map(readingText)))].filter(
    (text) => text !== '',
  );
  if (texts.length === 0) return undefined;
  const more = texts.length - SHOWN_READINGS;
  return [...texts.slice(0, SHOWN_READINGS), ...(more > 0 ? [`+${more} more`] : [])].join('; ');
}
